/* Preorder acknowledgement in the theme cart. Not a server-side checkout rule. */
(() => {
  if (customElements.get('mts-cart-consent')) return;
  let writes = Promise.resolve();

  class MtsCartConsent extends HTMLElement {
    connectedCallback() {
      this.pending = false;
      this.allowSubmit = false;
      this.stale = false;
      this.signature = null;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-mts-consent]')) return;
        this.readChecks();
        this.showError('');
        this.updateButton();
        this.save().catch(() => this.showError('Nie udało się zapisać potwierdzenia. Spróbuj ponownie przed przejściem do płatności.'));
      }, options);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[name="checkout"]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkout(button.form, button);
      }, { ...options, capture: true });
      document.addEventListener('submit', (event) => {
        const button = this.querySelector('[name="checkout"]');
        if (!button || event.target !== button.form) return;
        if (event.submitter && event.submitter.name !== 'checkout') return;
        if (this.allowSubmit) {
          this.allowSubmit = false;
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkout(event.target, button);
      }, { ...options, capture: true });
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['data-preorder-signature', 'data-accepted-signature', 'data-preorder-accepted', 'data-cart-lines'],
      });
      this.refresh();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    refresh() {
      const signature = this.dataset.preorderSignature || '';
      const changed = this.signature !== signature;
      const terms = this.querySelector('[data-mts-consent="terms"]');
      const preorder = this.querySelector('[data-mts-consent="preorder"]');
      if (this.signature === null) this.termsAccepted = Boolean(terms?.checked);
      if (changed) {
        this.preorderAccepted = Boolean(signature) &&
          this.dataset.preorderAccepted === 'true' &&
          this.dataset.acceptedSignature === signature;
        this.signature = signature;
      }
      if (terms) terms.checked = Boolean(this.termsAccepted);
      if (preorder) preorder.checked = Boolean(this.preorderAccepted);
      this.updateButton();
      if (changed && this.dataset.preorderAccepted === 'true' && !this.preorderAccepted) {
        this.save().catch(() => this.showError('Nie udało się zaktualizować zgody. Spróbuj ponownie.'));
      }
    }

    readChecks() {
      this.termsAccepted = Boolean(this.querySelector('[data-mts-consent="terms"]')?.checked);
      this.preorderAccepted = Boolean(this.querySelector('[data-mts-consent="preorder"]')?.checked);
    }

    satisfied() {
      return Array.from(this.querySelectorAll('[data-mts-consent]')).every((input) => input.checked);
    }

    updateButton() {
      const button = this.querySelector('[name="checkout"]');
      if (!button) return;
      button.disabled = this.dataset.empty === 'true' || this.pending || this.stale || !this.satisfied();
      button.setAttribute('aria-disabled', String(button.disabled));
      button.style.setProperty('opacity', button.disabled ? '0.45' : '1', 'important');
    }

    showError(message) {
      const error = this.querySelector('[data-mts-consent-error]');
      if (!error) return;
      if (error.textContent !== message) error.textContent = message;
      error.hidden = !message;
    }

    cartFingerprint(lines) {
      return JSON.stringify(lines.map((line) => [String(line.key), Number(line.quantity)])
        .sort((a, b) => a[0].localeCompare(b[0])));
    }

    attributes() {
      const accepted = Boolean(this.signature && this.preorderAccepted);
      const attributes = {
        'Akceptacja przedsprzedaży': accepted ? 'Tak' : '',
        'Warunki przedsprzedaży': accepted ? this.querySelector('[data-mts-preorder-text]').textContent.trim().replace(/\s+/g, ' ') : '',
        'Potwierdzenie przedsprzedaży — data UTC': accepted ? new Date().toISOString() : '',
        'mts_preorder_signature': accepted ? this.signature : '',
      };
      const terms = this.querySelector('[data-mts-consent="terms"]');
      if (terms) attributes['Akceptacja regulaminu'] = terms.checked ? 'Tak' : '';
      return attributes;
    }

    save() {
      const attributes = this.attributes();
      const root = window.Shopify?.routes?.root || '/';
      const operation = writes.catch(() => {}).then(async () => {
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error('Cart update failed');
        const cart = await response.json();
        for (const [key, value] of Object.entries(attributes)) {
          if ((cart.attributes?.[key] || '') !== value) throw new Error('Consent was not saved');
        }
        return cart;
      });
      writes = operation;
      return operation;
    }

    async checkout(form, button) {
      if (this.pending || this.stale || !form) return;
      this.readChecks();
      if (!this.satisfied()) {
        this.showError('Zaznacz wymagane zgody przed przejściem do płatności.');
        this.querySelector('[data-mts-consent]:not(:checked)')?.focus();
        this.updateButton();
        return;
      }
      if (!form.reportValidity()) return;
      this.pending = true;
      this.showError('');
      this.updateButton();
      try {
        const baseline = this.cartFingerprint(JSON.parse(this.dataset.cartLines));
        const signature = this.signature;
        const cart = await this.save();
        if (this.cartFingerprint(cart.items) !== baseline || signature !== this.dataset.preorderSignature) {
          this.stale = true;
          this.showError('Koszyk zmienił się podczas zapisywania. Odśwież stronę i sprawdź ponownie zamówienie.');
          return;
        }
        if (!this.isConnected || !this.satisfied()) return;
        this.pending = false;
        this.updateButton();
        this.allowSubmit = true;
        form.requestSubmit(button);
        this.allowSubmit = false;
      } catch (error) {
        this.showError('Nie udało się zapisać potwierdzenia. Sprawdź połączenie i spróbuj ponownie.');
      } finally {
        this.pending = false;
        this.updateButton();
      }
    }
  }
  customElements.define('mts-cart-consent', MtsCartConsent);
})();